import { Injectable, NgZone, OnDestroy } from '@angular/core';
import { EnvironmentService } from 'projects/shared/services/environment.service';
import { LocalStorageService } from './local-storage.service';
import { AuthData } from '../models/auth/auth-data.model';
import * as signalR from '@microsoft/signalr';
import { BehaviorSubject, Subject } from 'rxjs';

/**
 * Payload for per-file ADR status updates during OCR/ADR processing.
 */
export interface DocOcrAdrStatusUpdatePayload {
  applicationId: number;
  docFileId: number;
  status: string;
  extractionGlobalDocumentTypeConfigurationId?: number;
}

/**
 * Payload emitted when ADR processing completes for a single doc file.
 */
export interface DocOcrAdrCompletePayload {
  applicationId: number;
  docFileId: number;
}

/**
 * Payload emitted when the AI task-matching service returns a result
 * for a bulk-uploaded document.
 */
export interface BulkUploadTaskMatchResultPayload {
  docFileId: number;
  fileName: string;
  matchedTaskId: number | null;
  matchedTaskDescription: string | null;
  confidence: number;
  requiresManualSelection: boolean;
  extractedDocumentTypeName: string | null;
}

/**
 * Payload for a new borrower/agent message posted via SignalR.
 */
export interface NewMessagePostedPayload {
  messageId: number;
  applicationId: number;
  borrowerId?: number;
  agentId?: number;
  senderId: string;
  senderFirstName?: string;
  senderLastName?: string;
  content: string;
  postTime: string;
  messageScope: string;
}

/**
 * Payload for typing indicator events.
 */
export interface TypingIndicatorPayload {
  applicationId: number;
  userId: string;
  displayName?: string;
}

/**
 * Payload emitted when a task is rejected by the task validation service.
 */
export interface TaskRejectedByValidationPayload {
  applicationId: number;
  loanDocTaskId: number;
  taskDescription: string;
  rejectionReason: string;
}

/**
 * Payload for borrower verification status updates (e.g. Truv).
 */
export interface BorrowerVerificationStatusPayload {
  borrowerVerificationOrderId: number;
  status: string;
}

/**
 * SignalR service for the borrower portal.
 *
 * Connects to the Docs API UserNotificationHub and exposes observables
 * for real-time document processing and bulk-upload task-matching events.
 *
 * Mirrors the AdminV2 SignalRService pattern but is scoped to
 * borrower-relevant events only.
 */
@Injectable()
export class SignalRService implements OnDestroy {

  /** Whether the SignalR hub connection is currently in the Connected state. */
  get isConnected(): boolean {
    return !!this._connection && this._connection.state === signalR.HubConnectionState.Connected;
  }

  // ---------------------------------------------------------------------------
  // Public observables
  // ---------------------------------------------------------------------------

  private _docOcrAdrStatusUpdate = new Subject<DocOcrAdrStatusUpdatePayload>();
  public docOcrAdrStatusUpdate$ = this._docOcrAdrStatusUpdate.asObservable();

  private _docOcrAdrComplete = new Subject<DocOcrAdrCompletePayload>();
  public docOcrAdrComplete$ = this._docOcrAdrComplete.asObservable();

  private _bulkUploadTaskMatchResult = new Subject<BulkUploadTaskMatchResultPayload>();
  public bulkUploadTaskMatchResult$ = this._bulkUploadTaskMatchResult.asObservable();

  private _newMessagePosted = new Subject<NewMessagePostedPayload>();
  public newMessagePosted$ = this._newMessagePosted.asObservable();

  private _typingIndicator = new Subject<TypingIndicatorPayload>();
  public typingIndicator$ = this._typingIndicator.asObservable();

  private _taskRejectedByValidation = new Subject<TaskRejectedByValidationPayload>();
  public taskRejectedByValidation$ = this._taskRejectedByValidation.asObservable();

  private _borrowerVerificationStatusUpdated = new Subject<BorrowerVerificationStatusPayload>();
  public borrowerVerificationStatusUpdated$ = this._borrowerVerificationStatusUpdated.asObservable();

  /** Emits `true` when the hub is connected, `false` when disconnected/reconnecting. */
  private _connected = new BehaviorSubject<boolean>(false);
  public connected$ = this._connected.asObservable();

  // ---------------------------------------------------------------------------
  // Internal state
  // ---------------------------------------------------------------------------

  private _connection: signalR.HubConnection | null = null;
  private _currentApplicationId: number | null = null;
  private _pendingLoanRegistrations = new Set<number>();
  private _handlersRegistered = false;
  private _destroyed = false;

  /** Timer handle for the post-connect WebSocket transport upgrade attempt */
  private _transportUpgradeTimer: ReturnType<typeof setTimeout> | null = null;

  /** How long (ms) after a successful connect to check whether the negotiated transport is Long Polling */
  private static readonly TRANSPORT_UPGRADE_DELAY_MS = 10000;

  /** Upper bound on Long Polling -> WebSockets upgrade attempts, so a network that only allows Long Polling never churns */
  private static readonly MAX_TRANSPORT_UPGRADE_ATTEMPTS = 2;

  /** Number of upgrade attempts made since the last connection that negotiated WebSockets */
  private _transportUpgradeAttempts = 0;

  constructor(
    private readonly _ngZone: NgZone,
    private readonly _environment: EnvironmentService,
    private readonly _localStorageService: LocalStorageService
  ) {}

  ngOnDestroy(): void {
    this._destroyed = true;
    this.disconnect();
  }

  /**
   * Returns true only when the SignalR client reports that the live connection fell back to Long Polling.
   *
   * The client sets features.inherentKeepAlive on the underlying HttpConnection exclusively for the Long
   * Polling transport. Property names survive the production bundle's minification, so this stays correct in
   * deployed builds - unlike a transport class-name comparison, which the minifier renames and which therefore
   * reported "Long Polling" for every connection. An unrecognized shape is treated as WebSockets so a client
   * change can never resurrect that churn.
   */
  private _isConnectionOnLongPolling(connection: signalR.HubConnection): boolean {
    const features = (connection as any)?.connection?.features;
    return features?.inherentKeepAlive === true;
  }

  /**
   * Checks once, shortly after a connection is established, whether it is using Long Polling
   * and, if so, attempts to reconnect using WebSockets.
   */
  private _startTransportUpgradeCheck(): void {
    this._stopTransportUpgradeCheck();

    if (this._transportUpgradeAttempts >= SignalRService.MAX_TRANSPORT_UPGRADE_ATTEMPTS) {
      return;
    }

    this._ngZone.runOutsideAngular(() => {
      this._transportUpgradeTimer = setTimeout(() => {
        this._transportUpgradeTimer = null;

        const connection = this._connection;
        if (!connection || connection.state !== signalR.HubConnectionState.Connected) {
          return;
        }

        if (!this._isConnectionOnLongPolling(connection)) {
          this._transportUpgradeAttempts = 0;
          return;
        }

        this._transportUpgradeAttempts++;
        console.info('[SignalR] Currently on Long Polling \u2014 attempting upgrade to WebSockets');
        this._connection = null;
        this._handlersRegistered = false;
        connection.stop().then(() => {
          if (!this._destroyed) {
            this.connect();
          }
        }).catch(err => {
          console.warn('[SignalR] Error stopping connection during transport upgrade:', err);
        });
      }, SignalRService.TRANSPORT_UPGRADE_DELAY_MS);
    });
  }

  private _stopTransportUpgradeCheck(): void {
    if (this._transportUpgradeTimer) {
      clearTimeout(this._transportUpgradeTimer);
      this._transportUpgradeTimer = null;
    }
  }

  // ---------------------------------------------------------------------------
  // Public API
  // ---------------------------------------------------------------------------

  /**
   * Initialise the SignalR connection and register all event handlers.
   * Safe to call multiple times; will no-op if already connected.
   */
  connect(): void {
    const connection = this._getOrCreateConnection();
    if (!connection) {
      // Missing auth data; retry in 1 second
      setTimeout(() => {
        if (!this._destroyed) {
          this.connect();
        }
      }, 1000);
      return;
    }
    if (!this._handlersRegistered) {
      this._registerHandlers(connection);
      this._handlersRegistered = true;
    }
    this._startConnection(connection);
  }

  /**
   * Stop the SignalR connection and clean up.
   */
  disconnect(): void {
    this._stopTransportUpgradeCheck();
    if (this._connection) {
      this._connection.stop().catch(err => console.error('Error stopping SignalR connection:', err));
      this._connection = null;
    }
    this._currentApplicationId = null;
    this._pendingLoanRegistrations.clear();
    this._handlersRegistered = false;
  }

  /**
   * Register for loan-specific events on the hub.
   * Must be called after connect() when the user navigates to a loan.
   */
  registerForLoanEvents(applicationId: number): void {
    this._pendingLoanRegistrations.add(applicationId);
    this._attemptLoanRegistration(applicationId);
  }

  /**
   * Notify the hub that the current user is typing in the messages component.
   */
  sendTypingIndicator(applicationId: number): void {
    if (this._connection && this._connection.state === signalR.HubConnectionState.Connected) {
      this._connection.invoke('SendTypingIndicatorAsync', applicationId)
        .catch(err => console.debug('Error sending typing indicator:', err));
    }
  }

  /**
   * Unregister from loan-specific events when leaving a loan page.
   */
  unregisterForLoanEvents(applicationId: number): void {
    this._pendingLoanRegistrations.delete(applicationId);
    if (this._connection && this._connection.state === signalR.HubConnectionState.Connected) {
      this._currentApplicationId = null;
      this._connection.invoke('RemoveRegistrationForLoanEventsAsync', applicationId)
        .catch(err => console.error('Error unregistering for loan events:', err));
    }
  }

  // ---------------------------------------------------------------------------
  // Connection management (private)
  // ---------------------------------------------------------------------------

  private _getOrCreateConnection(): signalR.HubConnection | null {
    const authData = this._localStorageService.authorizationData as AuthData;
    if (!authData?.token) {
      return null;
    }

    // Re-use existing connection if not disconnected
    if (
      this._connection &&
      this._connection.state !== signalR.HubConnectionState.Disconnected &&
      this._connection.state !== signalR.HubConnectionState.Disconnecting
    ) {
      return this._connection;
    }

    const hubBaseUrl = this._environment.apiInfo.docApiBaseUrl || this._environment.apiInfo.apiBaseUrl;

    const refreshTokenAsync = async (): Promise<string> => {
      try {
        const authApiBase = this._environment.apiInfo.authApiBaseUrl || this._environment.apiInfo.apiBaseUrl;
        // Refresh from the credentials currently in storage: the captured authData can be several rotations
        // stale because the HTTP interceptor refreshes independently of this connection.
        const latestAuthData = this._localStorageService.authorizationData as AuthData;
        if (!latestAuthData?.refreshToken) {
          throw new Error('No refresh token available to refresh the SignalR access token');
        }
        const response = await fetch(`${authApiBase}/api/auth/refreshToken?refreshToken=${encodeURIComponent(latestAuthData.refreshToken)}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({})
        });
        if (!response.ok) {
          throw new Error('Failed to refresh token');
        }
        const data = await response.json();
        // A failed refresh also comes back as a 200 carrying an error body, so both tokens have to be present
        // before storing: writing an incomplete response would replace usable credentials with undefined and
        // force the user to log in again.
        if (!data?.jwtToken || !data?.refreshToken) {
          throw new Error('Refresh response did not contain usable tokens');
        }
        latestAuthData.token = data.jwtToken;
        latestAuthData.refreshToken = data.refreshToken;
        this._localStorageService.authorizationData = latestAuthData;
        return latestAuthData.token;
      } catch (error) {
        console.error('Error refreshing token for SignalR:', error);
        throw error;
      }
    };

    const connection = new signalR.HubConnectionBuilder()
      .configureLogging(signalR.LogLevel.Information)
      .withUrl(`${hubBaseUrl}/usernotificationhub`, {
        accessTokenFactory: async () => {
          try {
            if (this._isTokenExpired(authData.token)) {
              // Re-check from storage in case another tab refreshed
              const freshAuth = this._localStorageService.authorizationData as AuthData;
              if (this._isTokenExpired(freshAuth?.token)) {
                return await refreshTokenAsync();
              }
              return freshAuth.token;
            }
            return authData.token;
          } catch (error) {
            console.error('Failed to get token for SignalR:', error);
            throw error;
          }
        },
        transport: signalR.HttpTransportType.WebSockets | signalR.HttpTransportType.LongPolling,
      })
      .withAutomaticReconnect({
        nextRetryDelayInMilliseconds: (retryContext) => {
          return retryContext.previousRetryCount < 10 ? 5000 : 30000;
        }
      })
      .build();

    this._connection = connection;
    return connection;
  }

  private _startConnection(connection: signalR.HubConnection): void {
    connection.onclose(error => {
      this._connected.next(false);
      if (error && !this._destroyed) {
        console.warn('SignalR connection closed with error, attempting manual reconnect:', error);
        this._connection = null;
        setTimeout(() => {
          if (!this._destroyed) {
            this.connect();
          }
        }, 2000);
      }
    });

    connection.onreconnecting(_error => {
      console.debug('[SignalR] Reconnecting...');
      this._connected.next(false);
    });

    connection.onreconnected(_connectionId => {
      console.debug('[SignalR] Reconnected');
      this._connected.next(true);
      // Re-register pending loan registrations
      this._pendingLoanRegistrations.forEach(appId => {
        this._attemptLoanRegistration(appId);
      });
    });

    connection.start()
      .then(() => {
        console.debug('[SignalR] Connected');
        this._connected.next(true);
        // Process any pending registrations
        this._pendingLoanRegistrations.forEach(appId => {
          this._attemptLoanRegistration(appId);
        });

        // Start periodic transport upgrade check
        this._startTransportUpgradeCheck();
      })
      .catch(err => {
        console.error('[SignalR] Connection start failed:', err);
      });
  }

  private _attemptLoanRegistration(applicationId: number): void {
    if (this._connection && this._connection.state === signalR.HubConnectionState.Connected) {
      this._currentApplicationId = applicationId;
      this._connection.invoke('RegisterForLoanEventsAsync', applicationId)
        .then(() => {
          this._pendingLoanRegistrations.delete(applicationId);
        })
        .catch(err => {
          console.error('Error registering for loan events:', err);
          // Retry after delay
          setTimeout(() => {
            if (!this._destroyed) {
              this._attemptLoanRegistration(applicationId);
            }
          }, 2000);
        });
    }
  }

  // ---------------------------------------------------------------------------
  // Event handlers (private)
  // ---------------------------------------------------------------------------

  /**
   * Helper to register a SignalR handler outside Angular zone and re-enter
   * when the subject emits (same pattern as AdminV2 SignalRService).
   */
  private _registerHandler<T>(
    connection: signalR.HubConnection,
    eventName: string,
    handler: (payload: T) => void
  ): void {
    this._ngZone.runOutsideAngular(() => {
      connection.on(eventName, (payload: T) => {
        this._ngZone.run(() => handler(payload));
      });
    });
  }

  private _registerHandlers(connection: signalR.HubConnection): void {

    // --- ADR Status Update ---
    this._registerHandler<{ data: any }>(connection, 'DocOcrAdrStatusUpdate', (payload) => {
      const data = payload?.data || payload || {};
      const docFileId = data.docFileId ?? data.DocFileId;

      if (this._currentApplicationId && docFileId) {
        this._docOcrAdrStatusUpdate.next({
          applicationId: this._currentApplicationId,
          docFileId,
          status: data.status,
          extractionGlobalDocumentTypeConfigurationId: data.extractionGlobalDocumentTypeConfigurationId
        });
      }
    });

    // --- ADR Complete ---
    this._registerHandler<{ data: any }>(connection, 'DocOcrAdrComplete', (payload) => {
      const data = payload?.data || payload || {};
      const docFileId = data.docFileId ?? data.DocFileId;

      if (this._currentApplicationId && docFileId) {
        this._docOcrAdrComplete.next({
          applicationId: this._currentApplicationId,
          docFileId
        });
      }
    });

    // --- Bulk Upload Task Match Result ---
    this._registerHandler<{ data: any }>(connection, 'BulkUploadTaskMatchResult', (payload) => {
      const data = payload?.data || payload || {};

      this._bulkUploadTaskMatchResult.next({
        docFileId: data.docFileId ?? data.DocFileId,
        fileName: data.fileName ?? data.FileName ?? '',
        matchedTaskId: data.matchedTaskId ?? data.MatchedTaskId ?? null,
        matchedTaskDescription: data.matchedTaskDescription ?? data.MatchedTaskDescription ?? null,
        confidence: data.confidence ?? data.Confidence ?? 0,
        requiresManualSelection: data.requiresManualSelection ?? data.RequiresManualSelection ?? true,
        extractedDocumentTypeName: data.extractedDocumentTypeName ?? data.ExtractedDocumentTypeName ?? null
      });
    });

    // --- New Borrower Message Posted ---
    this._registerHandler<{ data: any }>(connection, 'NewBorrowerMessagePosted', (payload) => {
      const data = payload?.data || payload || {};
      this._newMessagePosted.next({
        messageId: data.messageId ?? data.MessageId,
        applicationId: data.applicationId ?? data.ApplicationId,
        borrowerId: data.borrowerId ?? data.BorrowerId,
        agentId: data.agentId ?? data.AgentId,
        senderId: data.senderId ?? data.SenderId ?? '',
        senderFirstName: data.senderFirstName ?? data.SenderFirstName,
        senderLastName: data.senderLastName ?? data.SenderLastName,
        content: data.content ?? data.Content ?? '',
        postTime: data.postTime ?? data.PostTime ?? new Date().toISOString(),
        messageScope: data.messageScope ?? data.MessageScope ?? 'BorrowerMessage'
      });
    });

    // --- New Agent Message Posted ---
    this._registerHandler<{ data: any }>(connection, 'NewAgentMessagePosted', (payload) => {
      const data = payload?.data || payload || {};
      this._newMessagePosted.next({
        messageId: data.messageId ?? data.MessageId,
        applicationId: data.applicationId ?? data.ApplicationId,
        borrowerId: data.borrowerId ?? data.BorrowerId,
        agentId: data.agentId ?? data.AgentId,
        senderId: data.senderId ?? data.SenderId ?? '',
        senderFirstName: data.senderFirstName ?? data.SenderFirstName,
        senderLastName: data.senderLastName ?? data.SenderLastName,
        content: data.content ?? data.Content ?? '',
        postTime: data.postTime ?? data.PostTime ?? new Date().toISOString(),
        messageScope: data.messageScope ?? data.MessageScope ?? 'AgentMessage'
      });
    });

    // --- Typing Indicator ---
    this._registerHandler<{ data: any }>(connection, 'TypingIndicator', (payload) => {
      const data = payload?.data || payload || {};
      this._typingIndicator.next({
        applicationId: data.applicationId ?? data.ApplicationId,
        userId: data.userId ?? data.UserId ?? '',
        displayName: data.displayName ?? data.DisplayName
      });
    });

    // --- Task Rejected By Validation ---
    this._registerHandler<{ data: any }>(connection, 'TaskRejectedByValidation', (payload) => {
      const data = payload?.data || payload || {};

      if (this._currentApplicationId) {
        this._taskRejectedByValidation.next({
          applicationId: data.applicationId ?? data.ApplicationId ?? this._currentApplicationId,
          loanDocTaskId: data.loanDocTaskId ?? data.LoanDocTaskId,
          taskDescription: data.taskDescription ?? data.TaskDescription ?? '',
          rejectionReason: data.rejectionReason ?? data.RejectionReason ?? ''
        });
      }
    });

    // --- Borrower Verification Status Updated (Truv) ---
    this._registerHandler<{ data: any }>(connection, 'BorrowerVerificationStatusUpdated', (payload) => {
      const data = payload?.data || payload || {};
      this._borrowerVerificationStatusUpdated.next({
        borrowerVerificationOrderId: data.borrowerVerificationOrderId ?? data.BorrowerVerificationOrderId,
        status: data.status ?? data.Status ?? ''
      });
    });
  }

  // ---------------------------------------------------------------------------
  // Utilities (private)
  // ---------------------------------------------------------------------------

  private _isTokenExpired(token: string | undefined | null): boolean {
    if (!token) return true;
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      return Date.now() >= payload.exp * 1000;
    } catch {
      return true;
    }
  }
}
